import React, { ForwardedRef, useCallback, useState } from 'react';
import { useConfig } from './config';
import { isNil } from '../utils/isNil';
import { msalLoginRedirect } from '../utils/auth/msal';
import { replaceNonAlphanumeric } from '../utils/replaceNonAlphanumeric';
import { useGetLitmosToken } from './useGetLitmosToken';
import { Api } from '../types/apiSpec';
import { isLearningPathType, isTrainingType } from '../utils/trainings/typesChecking';
import { useAssignUserToTraining } from '../api/postApi';
import { useViewContext } from '../context/ViewContext';
import useErrorMapping from './useErrorMapping';
import { VIDEO_PARAMS } from '../constants/courses';

type VideoParamKeys = (typeof VIDEO_PARAMS)[keyof typeof VIDEO_PARAMS];
export type LearningDetailsLinkParams = Partial<Record<VideoParamKeys, string>>;

export type GetDetailsLinkReturnType = {
  onClick?: (event: React.MouseEvent) => Promise<void>;
  href: string;
  isLoading: boolean;
};

export type UseGetDetailsParams = {
  originalId?: number | string;
  eventRegistrationUrl?: string;
  eventOriginalId?: number | string;
  name?: string;
  toRegisterPage?: boolean;
  ref?: ForwardedRef<HTMLAnchorElement>;
} & Pick<Api.EventLibrary, 'eventRegistrationUrl'>;

export type GetDetailsLink = (getDetailsParams: UseGetDetailsParams) => GetDetailsLinkReturnType;
export type UseGetDetailsLink = (args: {
  type: Api.LearningType;
  to?: DetailsLinkDestination;
  shouldSelfAssign?: boolean;
  id: string;
  params?: LearningDetailsLinkParams;
  eventId?: string;
}) => GetDetailsLink;

export enum DetailsLinkDestination {
  INTERNAL = 'internal',
  LITMOS = 'litmos',
  REGISTER = 'register',
}

const buildHref = (detailsPage: string, id: string, name?: string, params?: LearningDetailsLinkParams) => {
  return `${detailsPage}/${id}${name ? `/${replaceNonAlphanumeric(name)}` : ''}${
    params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : ''
  }`;
};

export const useGetDetailsLink: UseGetDetailsLink = ({ type, to, shouldSelfAssign, id, params }) => {
  const [litmosHref, setLitmosHref] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const config = useConfig();
  const { app } = useViewContext();
  const detailsPage = {
    [Api.LearningType.Microlearning]: config?.microlearningDetailsPage,
    [Api.LearningType.Document]: config?.documentDetailsPage,
    [Api.LearningType.Video]: config?.videoDetailsPage,
    [Api.LearningType.Course]: config?.courseDetailsPage,
    [Api.LearningType.LearningPath]: config?.learningPathDetailsPage,
    [Api.LearningType.Event]: config?.eventDetailsPage,
  }[type];
  const getToken = useGetLitmosToken();
  const assignToTraining = useAssignUserToTraining(
    type === Api.LearningType.Event ? Api.TrainingType.Course : (type as any),
  );
  const { triggerSnackbarError } = useErrorMapping();

  const handleRedirect = useCallback(async () => {
    setIsFetching(true);
    msalLoginRedirect();
  }, [isFetching]);

  const onClickHandler = useCallback(
    ({
        originalId,
        ref,
        eventOriginalId,
      }: {
        originalId?: number | string;
        eventOriginalId?: number | string;
        ref?: ForwardedRef<HTMLAnchorElement>;
      }) =>
      async (event: React.MouseEvent) => {
        if (type !== Api.LearningType.Event && !isTrainingType(type)) {
          return;
        }

        event.preventDefault();
        setIsFetching(true);

        event.stopPropagation();

        try {
          const data = await getToken();

          if (shouldSelfAssign && app.user?.userId) {
            await assignToTraining(app.user.userId, id);
          }

          const { loginURL } = data ?? {};
          const trainingTypeSuffix = isLearningPathType(type as any) ? '&lp=' : '&c=';
          const trainingWithIdSuffix = isNil(originalId)
            ? ''
            : (`${trainingTypeSuffix}${originalId}${eventOriginalId ? `&m=${eventOriginalId}` : ''}` as const);
          const currentLitmosHref = !loginURL ? '' : `${loginURL}${trainingWithIdSuffix}`;

          setLitmosHref(currentLitmosHref);

          /* eslint-disable no-param-reassign */
          if (ref && typeof ref !== 'function' && ref.current) {
            ref.current.href = currentLitmosHref;
            ref.current.click();
          }
        } catch (e: any) {
          triggerSnackbarError(e);
        } finally {
          setIsFetching(false);
        }
      },
    [shouldSelfAssign, app.user?.userId],
  );

  return useCallback(
    ({ originalId, eventRegistrationUrl, ref, name, eventOriginalId }: UseGetDetailsParams) => {
      const detailsHref = buildHref(detailsPage ?? '', id, name, params);

      if (eventRegistrationUrl) {
        return {
          href: eventRegistrationUrl,
          isLoading: isFetching,
        };
      }

      if (to === DetailsLinkDestination.REGISTER) {
        return {
          href: '#',
          onClick: handleRedirect,
          isLoading: isFetching,
        };
      }

      if (to === DetailsLinkDestination.LITMOS) {
        return {
          onClick: onClickHandler({ originalId, ref, eventOriginalId }),
          href: litmosHref,
          isLoading: isFetching,
        };
      }

      return {
        href: detailsHref,
        isLoading: false,
      };
    },
    [detailsPage, isFetching, litmosHref, shouldSelfAssign, type, onClickHandler],
  );
};
